import {
	Alert,
	Box,
	Button,
	Icons,
	Stack,
	Text,
	Spinner,
	Inline,
	RelativeTime,
	Heading,
} from "@sembark-travel/ui-base"
import { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router"
import useSWR from "swr"
import {
	isUserDeviceSubscribedForPushNotifications,
	askNotificationPermission,
	getDeviceNotificationPermissionsStatus,
	doesBrowserSupportPushNotifications,
	subscribeUserDeviceForPushNotifications,
	unsubscribeUserDeviceForPushNotifications,
	describePushRegistrationError,
	isPushRegistrationSuppressed,
	clearPushRegistrationFailure,
	subscribeToPushSubscriptionChanges,
	whenPushOpsIdle,
} from "./PushNotifications"
import { useAuthUser } from "../Auth"
import { useXHR } from "@sembark-travel/xhr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { Form, withServerErrors } from "@sembark-travel/ui-form"
import { useNotificationSoundMute } from "./sound"

// In-app sound preference: mute/unmute the notification "ding" across the app
// (the WhatsApp inbox and, in future, realtime general notifications). Stored
// locally per device; unmuting plays a short confirmation sound.
function InAppSoundPreference() {
	const { muted, toggle } = useNotificationSoundMute()
	return (
		<Stack gap="1">
			<Text fontWeight="semibold" fontSize="md">
				Notification sound
			</Text>
			<Inline gap="3" alignItems="center" flexWrap="wrap">
				<Text>
					{muted
						? "In-app notification sounds are muted on this device."
						: "A sound plays for new in-app notifications on this device."}
				</Text>
				<Button
					level={muted ? "primary" : "tertiary"}
					size="sm"
					onClick={toggle}
				>
					{muted ? (
						<>
							<Icons.SpeakerWave /> Unmute
						</>
					) : (
						<>
							<Icons.SpeakerXMark /> Mute
						</>
					)}
				</Button>
			</Inline>
		</Stack>
	)
}

export function NotificationPreferences() {
	const [permissionsStatus, setPermissionsStatus] = useState<
		"pending" | NotificationPermission
	>(() => getDeviceNotificationPermissionsStatus())
	const [isDesktopNotificationSupported] = useState(() =>
		doesBrowserSupportPushNotifications()
	)
	// Set when the user grants permission from THIS page, so we auto-subscribe
	// right after (a grant carried over from the top alert uses ?subscribe=1
	// instead — see DeviceRegistrationStatus).
	const [grantedOnPage, setGrantedOnPage] = useState(false)
	return (
		<Stack gap="6">
			<InAppSoundPreference />
			<Stack gap="4">
				<Stack gap="1">
					<Heading>Push Notifications</Heading>
					<Text color="muted">
						Enable desktop notifications, register this device, and send a test
						to make sure alerts reach you.
					</Text>
				</Stack>
				<Box>
					{!isDesktopNotificationSupported ? (
						<Alert status="warning">
							Desktop notifications aren't supported in this browser. Please use
							Google Chrome, Microsoft Edge, or Mozilla Firefox on a computer.
							(On iPhone/iPad, add this site to your Home Screen on iOS 16.4+ to
							receive notifications.)
						</Alert>
					) : (
						<Box>
							{permissionsStatus === "default" ? (
								<Stack gap="4">
									<Text>
										We strongly recommend enabling notifications so that you'll
										know when important activity happens in your Sembark
										workspace.
									</Text>
									<Box>
										<Button
											level="primary"
											onClick={() => {
												askNotificationPermission((status) => {
													setPermissionsStatus(status)
													if (status === "granted") setGrantedOnPage(true)
												})
												setPermissionsStatus("pending")
											}}
										>
											<Icons.Bell /> Enable Desktop Notifications
										</Button>
									</Box>
								</Stack>
							) : permissionsStatus === "pending" ? (
								<Box>
									<Alert status="info">
										Click <b>Allow</b> in your browsers's pop-up to enable
										notifications.
									</Alert>
								</Box>
							) : permissionsStatus === "denied" ? (
								<Alert status="warning">
									You've disallowed notifications in your browser. You'll need
									to open your browser preferences to change that.
								</Alert>
							) : permissionsStatus === "granted" ? (
								<DeviceRegistrationStatus autoSubscribe={grantedOnPage} />
							) : null}
						</Box>
					)}
				</Box>
			</Stack>
		</Stack>
	)
}

type TTestProgress = {
	send: "idle" | "sending" | "sent" | "failed"
	delivery: "pending" | "delivered" | "no_device" | "gateway_failed"
	open: "opened" | "not_opened"
}

const TEST_POLL_INTERVAL = 3000 // ms
const TEST_POLL_MAX_ATTEMPTS = 15 // ~45s

type TStageState = "idle" | "active" | "done" | "fail"

function TestStageIndicator({ state }: { state: TStageState }) {
	if (state === "done") {
		return (
			<Text color="success">
				<Icons.OkCircleSolid />
			</Text>
		)
	}
	if (state === "fail") {
		return (
			<Text color="danger" fontWeight="semibold">
				<Icons.CancelCircleSolid />
			</Text>
		)
	}
	if (state === "active") {
		return <Icons.Refresh spin />
	}
	return (
		<Text color="muted">
			<Icons.Preview />
		</Text>
	)
}

function TestStageRow({
	state,
	label,
	detail,
}: {
	state: TStageState
	label: string
	detail?: string
}) {
	return (
		<Inline gap="3" alignItems="center">
			<TestStageIndicator state={state} />
			<Stack gap="1">
				<Text color={state === "idle" ? "muted" : undefined}>{label}</Text>
				{detail ? (
					<Text fontSize="sm" color={state === "fail" ? "danger" : "muted"}>
						{detail}
					</Text>
				) : null}
			</Stack>
		</Inline>
	)
}

// Three-stage progression of the test notification, driven by the Beams
// delivery webhook and polled from /push-notifications/test/status.
function TestProgressChecklist({ test }: { test: TTestProgress }) {
	const sentState: TStageState =
		test.send === "sent" ? "done" : test.send === "failed" ? "fail" : "active"
	const deliveryState: TStageState =
		test.send !== "sent"
			? "idle"
			: test.delivery === "delivered"
				? "done"
				: test.delivery === "no_device" || test.delivery === "gateway_failed"
					? "fail"
					: "active"
	const openState: TStageState =
		test.send !== "sent" ? "idle" : test.open === "opened" ? "done" : "active"
	return (
		<Stack gap="2">
			<TestStageRow
				state={sentState}
				label="Sent"
				detail={
					test.send === "failed"
						? "We couldn't send the notification — try again."
						: undefined
				}
			/>
			<TestStageRow
				state={deliveryState}
				label="Delivered to your device"
				detail={
					test.delivery === "no_device"
						? "No device is registered — use “Register This Device”."
						: test.delivery === "gateway_failed"
							? "The push gateway rejected the delivery."
							: undefined
				}
			/>
			<TestStageRow
				state={openState}
				label="Opened"
				detail={
					openState === "active"
						? "Click the notification when it appears."
						: undefined
				}
			/>
		</Stack>
	)
}

type TDeliveryState = {
	last_delivered_at: string | null
	last_acknowledged_at: string | null
	last_opened_at: string | null
	test_sent_at: string | null
	test_delivered_at: string | null
	test_opened_at: string | null
}

const DELIVERY_REVALIDATE_INTERVAL = 15000 // ms

function Stat({ label, at }: { label: string; at: string | null }) {
	return (
		<Stack gap="1">
			<Text fontSize="xs" color="muted">
				{label}
			</Text>
			{at ? (
				<Text fontSize="sm">
					<RelativeTime timestamp={at} />
				</Text>
			) : (
				<Text fontSize="sm" color="muted">
					—
				</Text>
			)}
		</Stack>
	)
}

// Per-user push delivery activity (fed by the Beams webhook), shown compactly
// as wrapping label/value columns. Auto-revalidates so new signals appear.
function DeliveryActivity() {
	const xhr = useXHR()
	const { data: state, error } = useSWR<TDeliveryState>(
		"push-notifications/state",
		() =>
			xhr
				.get<TDeliveryState>("/push-notifications/state")
				.then((resp) => resp.data),
		{ refreshInterval: DELIVERY_REVALIDATE_INTERVAL, revalidateOnFocus: true }
	)

	if (error) {
		return (
			<Alert status="error">
				{(error as Error)?.message || "Couldn't load delivery activity."}
			</Alert>
		)
	}
	// Load quietly — don't reserve space with a spinner.
	if (!state) return null

	const hasAny = Boolean(
		state.last_delivered_at ||
		state.last_opened_at ||
		state.last_acknowledged_at ||
		state.test_sent_at
	)
	if (!hasAny) return null

	return (
		<Stack gap="2">
			<Text fontWeight="semibold" fontSize="sm">
				Delivery activity
			</Text>
			<Inline gap="6" flexWrap="wrap">
				<Stat label="Last delivered" at={state.last_delivered_at} />
				<Stat label="Last opened" at={state.last_opened_at} />
				<Stat label="Last acknowledged" at={state.last_acknowledged_at} />
				<Stat label="Test sent" at={state.test_sent_at} />
				<Stat label="Test delivered" at={state.test_delivered_at} />
				<Stat label="Test opened" at={state.test_opened_at} />
			</Inline>
		</Stack>
	)
}

function DeviceRegistrationStatus({
	autoSubscribe,
}: {
	autoSubscribe: boolean
}) {
	const xhr = useXHR()
	const [searchParams, setSearchParams] = useSearchParams()
	// Auto-subscribe intent: either a fresh grant on this page (prop) or carried
	// from the top alert via ?subscribe=1. Never auto-subscribe on a plain visit,
	// otherwise an unsubscribe is immediately undone.
	const wantsAutoSubscribe =
		autoSubscribe || searchParams.get("subscribe") === "1"
	const [registrationStatus, setRegistrationStatus] = useState<
		undefined | boolean
	>(undefined)
	const [test, setTest] = useState<TTestProgress>({
		send: "idle",
		delivery: "pending",
		open: "not_opened",
	})
	const [testMessage, setTestMessage] = useState<{
		type: "success" | "error"
		message: string
	} | null>(null)
	const [registering, setRegistering] = useState(false)
	const autoRegisterTriedRef = useRef(false)
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const attemptsRef = useRef(0)
	const { user } = useAuthUser()
	const { uid: userId } = user || {}
	useEffect(() => {
		if (!userId) return
		let active = true
		const check = () =>
			isUserDeviceSubscribedForPushNotifications({ uid: userId })
				.then((yes) => active && setRegistrationStatus(yes))
				.catch(() => active && setRegistrationStatus(false))
		// The login sync may still be re-subscribing this device when we land
		// here; read after it settles, and re-read on later changes, so we don't
		// latch a transient mid-sync not-registered snapshot into the UI.
		whenPushOpsIdle().then(() => active && check())
		const unsubscribe = subscribeToPushSubscriptionChanges(check)
		// Fallback: never leave the UI spinning if the read never resolves.
		const timer = setTimeout(() => {
			if (active) {
				setRegistrationStatus((status) =>
					status === undefined ? false : status
				)
			}
		}, 10000)
		return () => {
			active = false
			unsubscribe()
			clearTimeout(timer)
		}
	}, [userId])

	const stopPolling = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current)
			pollRef.current = null
		}
	}, [])

	// Stop polling when the component unmounts.
	useEffect(() => stopPolling, [stopPolling])

	const startPolling = useCallback(() => {
		stopPolling()
		attemptsRef.current = 0
		pollRef.current = setInterval(() => {
			attemptsRef.current += 1
			if (attemptsRef.current > TEST_POLL_MAX_ATTEMPTS) {
				stopPolling()
				return
			}
			xhr
				.get("/push-notifications/test/status")
				.then((resp) => {
					const data = resp?.data as
						| {
								status?: string
								delivery?: TTestProgress["delivery"]
								open?: TTestProgress["open"]
						  }
						| undefined
					if (!data || data.status === "idle") return
					setTest((prev) => ({
						...prev,
						delivery: data.delivery ?? prev.delivery,
						open: data.open ?? prev.open,
					}))
					if (data.delivery === "delivered" && data.open === "opened") {
						stopPolling()
					}
				})
				.catch(() => undefined)
		}, TEST_POLL_INTERVAL)
	}, [stopPolling, xhr])

	const sendTestNotification = useCallback(() => {
		setTest({ send: "sending", delivery: "pending", open: "not_opened" })
		setTestMessage(null)
		xhr
			.post("/push-notifications/test")
			.then((resp) => {
				const data = resp?.data as { message?: string } | undefined
				setTest((prev) => ({ ...prev, send: "sent" }))
				setTestMessage({
					type: "success",
					message:
						data?.message ||
						"Test notification sent — watch the checklist below.",
				})
				startPolling()
			})
			.catch((error) => {
				const e = error as Error
				setTest((prev) => ({ ...prev, send: "failed" }))
				setTestMessage({
					type: "error",
					message:
						e.message ||
						"Could not send the test notification. Please try again.",
				})
			})
	}, [startPolling, xhr])

	// Register this device with Pusher Beams. Guards unsupported browsers
	// (Safari / iOS) up front, where the subscription would fail. On success we
	// immediately send a test so the user can confirm delivery end-to-end.
	const registerDevice = useCallback(() => {
		if (!user) return
		if (!doesBrowserSupportPushNotifications()) {
			showSnackbar(
				"Desktop notifications aren't supported in this browser. Please use Chrome, Edge, or Firefox on a computer."
			)
			return
		}
		// An explicit attempt: clear any prior failure so a device that was
		// suppressed after failing can try again (e.g. after fixing a setting).
		clearPushRegistrationFailure()
		setRegistering(true)
		subscribeUserDeviceForPushNotifications(user)
			.then(() => {
				showSnackbar("This device is now registered to receive notifications.")
				setRegistrationStatus(true)
				sendTestNotification()
			})
			.catch(async (error) => {
				window.alert(await describePushRegistrationError(error))
				setRegistrationStatus(false)
			})
			.then(() => setRegistering(false))
	}, [user, sendTestNotification])

	// Finish setup automatically only when there's explicit intent (fresh grant
	// on this page, or ?subscribe=1 from the top alert) — not on every visit, so
	// an unsubscribe sticks. A failure leaves the manual button as a fallback.
	useEffect(() => {
		if (
			registrationStatus === false &&
			wantsAutoSubscribe &&
			!autoRegisterTriedRef.current &&
			doesBrowserSupportPushNotifications() &&
			// Don't auto-retry once a registration has failed on this device.
			user &&
			!isPushRegistrationSuppressed(user)
		) {
			autoRegisterTriedRef.current = true
			// Consume the query param so a refresh doesn't re-subscribe.
			if (searchParams.get("subscribe")) {
				searchParams.delete("subscribe")
				setSearchParams(searchParams, { replace: true })
			}
			registerDevice()
		}
	}, [
		registrationStatus,
		wantsAutoSubscribe,
		registerDevice,
		searchParams,
		setSearchParams,
		user,
	])

	if (!user) return null
	return (
		<Stack gap="4">
			{registrationStatus === undefined ? (
				<Box>
					<Spinner />
				</Box>
			) : registrationStatus ? (
				<Stack gap="4">
					<Text>This device is registered to receive notifications.</Text>
					<Inline gap="4">
						<Button
							size="sm"
							disabled={test.send === "sending"}
							onClick={sendTestNotification}
						>
							{test.send === "sending"
								? "Sending…"
								: "Send a Test Notification"}
						</Button>
						<Form<unknown>
							onSubmit={withServerErrors(async () => {
								unsubscribeUserDeviceForPushNotifications().then((status) =>
									setRegistrationStatus(status)
								)
								// also delete other subscriptions
								await xhr.delete("/push-notifications/subscriptions")
							})}
							subscription={{ submitting: true }}
						>
							{({ handleSubmit, submitting }) => (
								<form onSubmit={handleSubmit} noValidate>
									<Button type="submit" size="sm" disabled={submitting}>
										{submitting ? "Please wait..." : "Unsubscribe"}
									</Button>
								</form>
							)}
						</Form>
					</Inline>
					{testMessage ? (
						<Alert
							status={testMessage.type === "success" ? "success" : "error"}
						>
							{testMessage.message}
						</Alert>
					) : null}
					{test.send !== "idle" ? <TestProgressChecklist test={test} /> : null}
					<DeliveryActivity />
					<Stack gap="2">
						<Text fontWeight="semibold">Not receiving notifications?</Text>
						<Text color="muted">
							Test notifications are delivered by your browser, so a delivery
							can be silently blocked outside this app. If a test does not
							arrive within a minute, check the following:
						</Text>
						<Box as="ul" paddingLeft="6" listStyleType="disc">
							<Text as="li">
								<b>Browser site settings</b>: allow notifications for this site
								(the lock/site icon in the address bar → Notifications → Allow).
							</Text>
							<Text as="li">
								<b>Operating system settings</b>: enable notifications for your
								browser, and turn off <b>Do Not Disturb</b> / <b>Focus</b> mode.
							</Text>
							<Text as="li">
								<b>This device</b>: make sure it shows as registered above
								(re-register if you recently cleared site data).
							</Text>
							<Text as="li">
								Still nothing? Try a supported, up-to-date browser (Chrome,
								Edge, Firefox, or Safari).
							</Text>
						</Box>
					</Stack>
				</Stack>
			) : (
				<Stack gap="4">
					<Text fontWeight="semibold" color="success">
						Desktop Notifications have been enabled.
					</Text>
					<Text>
						{registering
							? "Registering this device to receive notifications…"
							: "This device isn't registered yet. Use the button below to register it."}
					</Text>
					<Box>
						<Button
							level="primary"
							disabled={registering}
							onClick={registerDevice}
						>
							{registering ? (
								<>
									<Icons.Refresh spin /> Registering Device. Please wait...
								</>
							) : (
								<>
									<Icons.OkCircleSolid /> Register This Device
								</>
							)}
						</Button>
					</Box>
				</Stack>
			)}
		</Stack>
	)
}
